// lib/taxonomyAdmin.ts
import {
  collection,
  collectionGroup,
  doc,
  getDoc,
  getDocs,
  query,
  where,
  orderBy,
  limit,
  setDoc,
  updateDoc,
  addDoc,
  serverTimestamp,
  type DocumentData,
  type Firestore,
} from "firebase/firestore";
import { normalizeLabel } from "@/lib/taxonomy";

export type ModerationStatus = "approved" | "pending" | "rejected";

const CATEGORIES = "categories";
const SUBCATEGORIES = "subcategories";
const CATEGORY_SUGGESTIONS = "categorySuggestions";
const BUSINESSES = "businesses";

export type TaxonomySuggestionType = "category" | "subcategory";

type LinkedBusinessTaxonomySuggestion = {
  businessId: string;
  businessName: string;
  accountNumber: string;
  ownerId: string;
  status: string;
  businessPath: string;
};

/**
 * Firestore structure:
 *   categories/{categoryId}/subcategories/{subcategoryId}
 *
 * ✅ Category ops use: categories (top-level)
 * ✅ Subcategory ops use: nested subcollection under a category
 *
 * For "Pending" (global moderation), we use:
 *   collectionGroup("subcategories")
 */

function pickNormalized(d: any) {
  return typeof d?.nameNormalized === "string"
    ? d.nameNormalized
    : typeof d?.normalized === "string"
    ? d.normalized
    : normalizeLabel(d?.name ?? "");
}

function cleanText(value: unknown) {
  return typeof value === "string" ? value.trim() : "";
}

function cleanDisplayText(value: unknown): string {
  if (Array.isArray(value)) {
    return value
      .map((item) => cleanDisplayText(item))
      .filter(Boolean)
      .join(", ");
  }

  if (value && typeof value === "object") {
    const record = value as Record<string, unknown>;
    return (
      cleanText(record.name) ||
      cleanText(record.label) ||
      cleanText(record.title)
    );
  }

  return cleanText(value);
}

function cleanFirstDisplayText(...values: unknown[]) {
  for (const value of values) {
    const text = cleanDisplayText(value);
    if (text) return text;
  }

  return "";
}

function cleanPatch(values: Record<string, unknown>) {
  const out: Record<string, unknown> = {};

  for (const [key, value] of Object.entries(values)) {
    if (value !== undefined) {
      out[key] = value;
    }
  }

  return out;
}

function uniqueStrings(values: unknown[]) {
  return Array.from(
    new Set(
      values
        .filter((value): value is string => typeof value === "string")
        .map((value) => value.trim())
        .filter(Boolean)
    )
  );
}

function readLinkedBusiness(
  id: string,
  path: string,
  data: DocumentData
): LinkedBusinessTaxonomySuggestion {
  return {
    businessId: id,
    businessName: cleanFirstDisplayText(data.name, data.businessName),
    accountNumber: cleanText(data.accountNumber),
    ownerId: cleanText(data.ownerId),
    status: cleanText(data.status),
    businessPath: path,
  };
}

function getCategoryId(value: unknown, categoryName: string) {
  return cleanText(value) || normalizeLabel(categoryName);
}

function getSubcategoryId(value: unknown, subcategoryName: string) {
  return cleanText(value) || normalizeLabel(subcategoryName);
}

async function ensureApprovedParentCategory(
  db: Firestore,
  args: {
    categoryId: string;
    categoryName: string;
    adminUid: string;
    createdBy?: unknown;
  }
) {
  const categoryRef = doc(db, CATEGORIES, args.categoryId);
  const categorySnap = await getDoc(categoryRef);

  if (categorySnap.exists()) {
    const data = categorySnap.data();

    if (data?.status === "approved" && data?.isDeleted !== true) {
      return String(data.name ?? args.categoryName);
    }

    throw new Error("Approve the parent category before this subcategory.");
  }

  const pendingCategorySuggestion = await getDoc(
    doc(db, CATEGORY_SUGGESTIONS, `category_${args.categoryId}`)
  );

  if (pendingCategorySuggestion.exists()) {
    const data = pendingCategorySuggestion.data();

    if (data?.status === "pending") {
      throw new Error("Approve the parent category before this subcategory.");
    }
  }

  if (!args.categoryName) {
    throw new Error("Subcategory suggestion is missing categoryName.");
  }

  await setDoc(
    categoryRef,
    {
      id: args.categoryId,
      name: args.categoryName,
      nameNormalized: normalizeLabel(args.categoryName),
      status: "approved",
      isDeleted: false,
      sortOrder: 1000,

      createdAt: serverTimestamp(),
      createdBy: args.createdBy ?? "",
      updatedAt: serverTimestamp(),
      updatedBy: args.adminUid,

      reviewedAt: serverTimestamp(),
      reviewedBy: args.adminUid,
      reviewNote: "Created while approving subcategory suggestion",
    },
    { merge: true }
  );

  return args.categoryName;
}

async function markLinkedBusinessesTaxonomyApproved(
  db: Firestore,
  args: {
    type: TaxonomySuggestionType;
    businessIds: string[];
    categoryId: string;
    categoryName: string;
    subcategoryId?: string;
    subcategoryName?: string;
  }
) {
  const businessIds = uniqueStrings(args.businessIds);
  if (businessIds.length === 0) return;

  const categoryPatch =
    args.categoryId && args.categoryName
      ? {
          categoryId: args.categoryId,
          categoryName: args.categoryName,
          categoryNameNormalized: normalizeLabel(args.categoryName),
          categoryStatus: "approved",
          category: args.categoryName,
        }
      : {};

  const subcategoryPatch =
    args.type === "subcategory" && args.subcategoryName
      ? {
          subcategoryId:
            args.subcategoryId || normalizeLabel(args.subcategoryName),
          subcategoryName: args.subcategoryName,
          subcategoryNameNormalized: normalizeLabel(args.subcategoryName),
          subcategoryStatus: "approved",
          subCategoryText: args.subcategoryName,
          subCategory: [args.subcategoryName],
        }
      : {};

  for (const businessId of businessIds) {
    await updateDoc(
      doc(db, BUSINESSES, businessId),
      cleanPatch({
        ...categoryPatch,
        ...subcategoryPatch,
        updatedAt: serverTimestamp(),
      })
    );
  }
}

async function clearLinkedBusinessesRejectedTaxonomy(
  db: Firestore,
  args: {
    type: TaxonomySuggestionType;
    businessIds: string[];
  }
) {
  const businessIds = uniqueStrings(args.businessIds);
  if (businessIds.length === 0) return;

  const patch =
    args.type === "category"
      ? {
          categoryId: null,
          categoryName: "",
          categoryNameNormalized: "",
          categoryStatus: "approved",
          category: "",
          subcategoryId: null,
          subcategoryName: "",
          subcategoryNameNormalized: "",
          subcategoryStatus: "approved",
          subCategoryText: "",
          subCategory: [],
        }
      : {
          subcategoryId: null,
          subcategoryName: "",
          subcategoryNameNormalized: "",
          subcategoryStatus: "approved",
          subCategoryText: "",
          subCategory: [],
        };

  for (const businessId of businessIds) {
    await updateDoc(doc(db, BUSINESSES, businessId), {
      ...patch,
      updatedAt: serverTimestamp(),
    });
  }
}

/** ---------------- LISTING ---------------- */

export async function adminListCategories(
  db: Firestore,
  args: { status?: ModerationStatus; includeDeleted?: boolean } = {}
) {
  const { status, includeDeleted } = args;

  const col = collection(db, CATEGORIES);
  const clauses: any[] = [];
  if (status) clauses.push(where("status", "==", status));
  if (!includeDeleted) clauses.push(where("isDeleted", "==", false));

  // If you don't have sortOrder, remove the first orderBy.
  const q = query(
    col,
    ...clauses,
    orderBy("sortOrder", "asc"),
    orderBy("name", "asc"),
    limit(500)
  );

  const snap = await getDocs(q);

  return snap.docs.map((d) => ({
    id: d.id,
    ...(d.data() as DocumentData),
    nameNormalized: pickNormalized(d.data()),
  }));
}

/**
 * List subcategories for ONE category (nested subcollection):
 * categories/{categoryId}/subcategories
 */
export async function adminListSubcategories(
  db: Firestore,
  args: {
    categoryId: string; // REQUIRED (because subcategories are nested)
    status?: ModerationStatus;
    includeDeleted?: boolean;
  }
) {
  const { categoryId, status, includeDeleted } = args;

  if (!categoryId) {
    throw new Error("adminListSubcategories: categoryId is required.");
  }

  const col = collection(db, CATEGORIES, categoryId, SUBCATEGORIES);
  const clauses: any[] = [];
  if (status) clauses.push(where("status", "==", status));
  if (!includeDeleted) clauses.push(where("isDeleted", "==", false));

  const q = query(
    col,
    ...clauses,
    orderBy("sortOrder", "asc"),
    orderBy("name", "asc"),
    limit(1000)
  );

  const snap = await getDocs(q);

  return snap.docs.map((d) => ({
    id: d.id,
    ...(d.data() as DocumentData),
    nameNormalized: pickNormalized(d.data()),
  }));
}

/**
 * ✅ List subcategories across ALL categories (global moderation / Pending tab)
 * Uses collectionGroup("subcategories") so categoryId is NOT required.
 *
 * NOTE: You already store categoryId inside each subcategory doc (good).
 */
export async function adminListSubcategoriesGlobal(
  db: Firestore,
  args: {
    status?: ModerationStatus;
    includeDeleted?: boolean;
    limitCount?: number;
    orderByField?: "name" | "sortOrder"; // optional knob
  } = {}
) {
  const { status, includeDeleted, limitCount, orderByField } = args;

  const col = collectionGroup(db, SUBCATEGORIES);
  const clauses: any[] = [];
  if (status) clauses.push(where("status", "==", status));
  if (!includeDeleted) clauses.push(where("isDeleted", "==", false));

  // Safer default for global query: order by "name".
  // If you want sortOrder + name globally, set orderByField: "sortOrder".
  const q =
    orderByField === "sortOrder"
      ? query(
          col,
          ...clauses,
          orderBy("sortOrder", "asc"),
          orderBy("name", "asc"),
          limit(typeof limitCount === "number" ? limitCount : 1000)
        )
      : query(
          col,
          ...clauses,
          orderBy("name", "asc"),
          limit(typeof limitCount === "number" ? limitCount : 1000)
        );

  const snap = await getDocs(q);

  return snap.docs.map((d) => {
    const data = d.data() as DocumentData;
    return {
      id: d.id,
      ...data,
      // helpful for UI/actions
      categoryId: data.categoryId,
      nameNormalized: pickNormalized(data),
      _path: d.ref.path, // optional debug
    };
  });
}

export async function adminListTaxonomySuggestions(
  db: Firestore,
  args: { status?: ModerationStatus; limitCount?: number } = {}
) {
  const { status, limitCount } = args;

  const clauses: any[] = [];
  if (status) clauses.push(where("status", "==", status));

  const q = query(
    collection(db, CATEGORY_SUGGESTIONS),
    ...clauses,
    limit(typeof limitCount === "number" ? limitCount : 500)
  );

  const snap = await getDocs(q);

  return snap.docs.map((d) => {
    const data = d.data() as DocumentData;
    const type = data.type === "subcategory" ? "subcategory" : "category";
    const name =
      type === "subcategory"
        ? String(data.subcategoryName ?? data.name ?? "")
        : String(data.categoryName ?? data.name ?? "");

    return {
      id:
        type === "subcategory"
          ? String(data.subcategoryId ?? d.id)
          : String(data.categoryId ?? d.id),
      ...data,
      type,
      name,
      nameNormalized: pickNormalized({ ...data, name }),
      linkedBusinesses: [] as LinkedBusinessTaxonomySuggestion[],
      _source: "categorySuggestions",
      _path: d.ref.path,
      _suggestionPath: d.ref.path,
    };
  });
}

export async function adminListBusinessTaxonomySuggestions(
  db: Firestore,
  args: { limitCount?: number } = {}
) {
  const limitCount =
    typeof args.limitCount === "number" ? args.limitCount : 500;
  const col = collection(db, BUSINESSES);

  const [categorySnap, subcategorySnap] = await Promise.all([
    getDocs(query(col, where("categoryStatus", "==", "pending"), limit(limitCount))),
    getDocs(
      query(col, where("subcategoryStatus", "==", "pending"), limit(limitCount))
    ),
  ]);

  const rows: DocumentData[] = [];

  categorySnap.docs.forEach((businessDoc) => {
    const data = businessDoc.data() as DocumentData;
    if (data.isDeleted === true) return;

    const categoryName = cleanFirstDisplayText(data.categoryName, data.category);
    if (!categoryName) return;

    const categoryId = getCategoryId(data.categoryId, categoryName);
    const linkedBusiness = readLinkedBusiness(
      businessDoc.id,
      businessDoc.ref.path,
      data
    );

    rows.push({
      id: categoryId,
      type: "category",
      name: categoryName,
      categoryId,
      categoryName,
      nameNormalized:
        cleanText(data.categoryNameNormalized) || normalizeLabel(categoryName),
      status: "pending",
      createdAt: data.updatedAt ?? data.createdAt,
      createdByUid: data.ownerId,
      createdBy: data.ownerId,
      businessId: businessDoc.id,
      businessName: linkedBusiness.businessName,
      accountNumber: linkedBusiness.accountNumber,
      ownerId: linkedBusiness.ownerId,
      linkedBusinesses: [linkedBusiness],
      _source: "businessPendingTaxonomy",
      _businessPath: businessDoc.ref.path,
      _path: businessDoc.ref.path,
    });
  });

  subcategorySnap.docs.forEach((businessDoc) => {
    const data = businessDoc.data() as DocumentData;
    if (data.isDeleted === true) return;

    const subcategoryName = cleanFirstDisplayText(
      data.subcategoryName,
      data.subCategoryText,
      data.subCategory,
      data.subcategory
    );
    if (!subcategoryName) return;

    const categoryName = cleanFirstDisplayText(data.categoryName, data.category);
    const categoryId = getCategoryId(data.categoryId, categoryName);
    if (!categoryId) return;

    const subcategoryId = getSubcategoryId(
      data.subcategoryId,
      subcategoryName
    );
    const linkedBusiness = readLinkedBusiness(
      businessDoc.id,
      businessDoc.ref.path,
      data
    );

    rows.push({
      id: subcategoryId,
      type: "subcategory",
      name: subcategoryName,
      categoryId,
      categoryName,
      subcategoryId,
      subcategoryName,
      nameNormalized:
        cleanText(data.subcategoryNameNormalized) ||
        normalizeLabel(subcategoryName),
      status: "pending",
      createdAt: data.updatedAt ?? data.createdAt,
      createdByUid: data.ownerId,
      createdBy: data.ownerId,
      businessId: businessDoc.id,
      businessName: linkedBusiness.businessName,
      accountNumber: linkedBusiness.accountNumber,
      ownerId: linkedBusiness.ownerId,
      linkedBusinesses: [linkedBusiness],
      _source: "businessPendingTaxonomy",
      _businessPath: businessDoc.ref.path,
      _path: businessDoc.ref.path,
    });
  });

  return rows;
}

/** ---------------- CREATE (admin) ---------------- */

export async function adminCreateApprovedCategory(
  db: Firestore,
  args: { name: string; adminUid: string; sortOrder?: number }
) {
  const name = args.name.trim();
  const nameNormalized = normalizeLabel(name);

  const ref = await addDoc(collection(db, CATEGORIES), {
    name,
    nameNormalized,
    status: "approved",
    isDeleted: false,
    sortOrder: typeof args.sortOrder === "number" ? args.sortOrder : 1000,

    createdAt: serverTimestamp(),
    createdBy: args.adminUid,
    updatedAt: serverTimestamp(),
    updatedBy: args.adminUid,

    reviewedAt: serverTimestamp(),
    reviewedBy: args.adminUid,
    reviewNote: "Created by admin",
  });

  return { id: ref.id };
}

export async function adminCreateApprovedSubcategory(
  db: Firestore,
  args: { categoryId: string; name: string; adminUid: string; sortOrder?: number }
) {
  const name = args.name.trim();
  const nameNormalized = normalizeLabel(name);

  // ✅ nested path: categories/{categoryId}/subcategories
  const ref = await addDoc(
    collection(db, CATEGORIES, args.categoryId, SUBCATEGORIES),
    {
      name,
      nameNormalized,
      status: "approved",
      isDeleted: false,
      sortOrder: typeof args.sortOrder === "number" ? args.sortOrder : 1000,

      // ✅ keep for collectionGroup queries + moderation UI
      categoryId: args.categoryId,

      createdAt: serverTimestamp(),
      createdBy: args.adminUid,
      updatedAt: serverTimestamp(),
      updatedBy: args.adminUid,

      reviewedAt: serverTimestamp(),
      reviewedBy: args.adminUid,
      reviewNote: "Created by admin",
    }
  );

  return { id: ref.id };
}

export async function adminApproveTaxonomySuggestion(
  db: Firestore,
  args: {
    suggestionPath: string;
    adminUid: string;
    note?: string;
    linkedBusinessIds?: string[];
  }
) {
  if (!args.suggestionPath) throw new Error("Missing suggestionPath");

  const suggestionRef = doc(db, args.suggestionPath);
  const suggestionSnap = await getDoc(suggestionRef);

  if (!suggestionSnap.exists()) {
    throw new Error("Suggestion not found.");
  }

  const data = suggestionSnap.data() as DocumentData;
  const type = data.type === "subcategory" ? "subcategory" : "category";
  const name =
    type === "subcategory"
      ? String(data.subcategoryName ?? data.name ?? "").trim()
      : String(data.categoryName ?? data.name ?? "").trim();
  const nameNormalized = String(data.nameNormalized ?? normalizeLabel(name));
  let approvedCategoryNameForLinked = type === "category" ? name : "";

  if (!name || !nameNormalized) {
    throw new Error("Suggestion is missing a valid name.");
  }

  if (type === "category") {
    const categoryId = String(data.categoryId ?? nameNormalized);

    await setDoc(
      doc(db, CATEGORIES, categoryId),
      {
        id: categoryId,
        name,
        nameNormalized,
        status: "approved",
        isDeleted: false,
        sortOrder: 1000,

        createdAt: serverTimestamp(),
        createdBy: data.createdByUid ?? data.createdBy ?? "",
        updatedAt: serverTimestamp(),
        updatedBy: args.adminUid,

        reviewedAt: serverTimestamp(),
        reviewedBy: args.adminUid,
        reviewNote: args.note ?? "",
      },
      { merge: true }
    );
  } else {
    const categoryId = String(data.categoryId ?? "").trim();
    const subcategoryId = String(data.subcategoryId ?? nameNormalized).trim();
    const categoryName = String(data.categoryName ?? "").trim();

    if (!categoryId || !subcategoryId) {
      throw new Error("Subcategory suggestion is missing categoryId.");
    }

    const approvedCategoryName = await ensureApprovedParentCategory(db, {
      categoryId,
      categoryName,
      adminUid: args.adminUid,
      createdBy: data.createdByUid ?? data.createdBy ?? "",
    });
    approvedCategoryNameForLinked = approvedCategoryName;

    await setDoc(
      doc(db, CATEGORIES, categoryId, SUBCATEGORIES, subcategoryId),
      {
        id: subcategoryId,
        name,
        nameNormalized,
        status: "approved",
        isDeleted: false,
        sortOrder: 1000,

        categoryId,
        categoryName: approvedCategoryName,

        createdAt: serverTimestamp(),
        createdBy: data.createdByUid ?? data.createdBy ?? "",
        updatedAt: serverTimestamp(),
        updatedBy: args.adminUid,

        reviewedAt: serverTimestamp(),
        reviewedBy: args.adminUid,
        reviewNote: args.note ?? "",
      },
      { merge: true }
    );
  }

  await updateDoc(suggestionRef, {
    status: "approved",
    reviewNote: args.note ?? "",
    reviewedAt: serverTimestamp(),
    reviewedBy: args.adminUid,
    updatedAt: serverTimestamp(),
    updatedBy: args.adminUid,
  });

  await markLinkedBusinessesTaxonomyApproved(db, {
    type,
    businessIds: args.linkedBusinessIds ?? [],
    categoryId:
      type === "category"
        ? String(data.categoryId ?? nameNormalized)
        : String(data.categoryId ?? ""),
    categoryName:
      type === "category"
        ? name
        : approvedCategoryNameForLinked,
    subcategoryId:
      type === "subcategory"
        ? String(data.subcategoryId ?? nameNormalized)
        : undefined,
    subcategoryName: type === "subcategory" ? name : undefined,
  });
}

export async function adminRejectTaxonomySuggestion(
  db: Firestore,
  args: {
    suggestionPath: string;
    adminUid: string;
    note?: string;
    linkedBusinessIds?: string[];
  }
) {
  if (!args.suggestionPath) throw new Error("Missing suggestionPath");

  const suggestionRef = doc(db, args.suggestionPath);
  const suggestionSnap = await getDoc(suggestionRef);
  const data = suggestionSnap.exists()
    ? (suggestionSnap.data() as DocumentData)
    : {};
  const type = data.type === "subcategory" ? "subcategory" : "category";

  await updateDoc(suggestionRef, {
    status: "rejected",
    reviewNote: args.note ?? "",
    reviewedAt: serverTimestamp(),
    reviewedBy: args.adminUid,
    updatedAt: serverTimestamp(),
    updatedBy: args.adminUid,
  });

  await clearLinkedBusinessesRejectedTaxonomy(db, {
    type,
    businessIds: args.linkedBusinessIds ?? [],
  });
}

export async function adminApproveBusinessTaxonomySuggestion(
  db: Firestore,
  args: {
    businessId: string;
    type: TaxonomySuggestionType;
    adminUid: string;
    categoryId?: string | null;
    categoryName?: string | null;
    subcategoryId?: string | null;
    subcategoryName?: string | null;
    note?: string;
  }
) {
  const businessSnap = await getDoc(doc(db, BUSINESSES, args.businessId));

  if (!businessSnap.exists()) {
    throw new Error("Business not found.");
  }

  const business = businessSnap.data() as DocumentData;
  const categoryName =
    cleanText(args.categoryName) ||
    cleanFirstDisplayText(business.categoryName, business.category);
  const categoryId = getCategoryId(args.categoryId ?? business.categoryId, categoryName);

  if (!categoryId || !categoryName) {
    throw new Error("Suggestion is missing a valid category.");
  }

  if (args.type === "category") {
    await setDoc(
      doc(db, CATEGORIES, categoryId),
      {
        id: categoryId,
        name: categoryName,
        nameNormalized: normalizeLabel(categoryName),
        status: "approved",
        isDeleted: false,
        sortOrder: 1000,

        createdAt: serverTimestamp(),
        createdBy: business.ownerId ?? "",
        updatedAt: serverTimestamp(),
        updatedBy: args.adminUid,

        reviewedAt: serverTimestamp(),
        reviewedBy: args.adminUid,
        reviewNote: args.note ?? "",
      },
      { merge: true }
    );

    await markLinkedBusinessesTaxonomyApproved(db, {
      type: "category",
      businessIds: [args.businessId],
      categoryId,
      categoryName,
    });

    return;
  }

  if (business.categoryStatus === "pending") {
    throw new Error("Approve the parent category before this subcategory.");
  }

  const subcategoryName =
    cleanText(args.subcategoryName) ||
    cleanFirstDisplayText(
      business.subcategoryName,
      business.subCategoryText,
      business.subCategory,
      business.subcategory
    );
  const subcategoryId = getSubcategoryId(
    args.subcategoryId ?? business.subcategoryId,
    subcategoryName
  );

  if (!subcategoryId || !subcategoryName) {
    throw new Error("Suggestion is missing a valid subcategory.");
  }

  const approvedCategoryName = await ensureApprovedParentCategory(db, {
    categoryId,
    categoryName,
    adminUid: args.adminUid,
    createdBy: business.ownerId ?? "",
  });

  await setDoc(
    doc(db, CATEGORIES, categoryId, SUBCATEGORIES, subcategoryId),
    {
      id: subcategoryId,
      name: subcategoryName,
      nameNormalized: normalizeLabel(subcategoryName),
      status: "approved",
      isDeleted: false,
      sortOrder: 1000,

      categoryId,
      categoryName: approvedCategoryName,

      createdAt: serverTimestamp(),
      createdBy: business.ownerId ?? "",
      updatedAt: serverTimestamp(),
      updatedBy: args.adminUid,

      reviewedAt: serverTimestamp(),
      reviewedBy: args.adminUid,
      reviewNote: args.note ?? "",
    },
    { merge: true }
  );

  await markLinkedBusinessesTaxonomyApproved(db, {
    type: "subcategory",
    businessIds: [args.businessId],
    categoryId,
    categoryName: approvedCategoryName,
    subcategoryId,
    subcategoryName,
  });
}

export async function adminRejectBusinessTaxonomySuggestion(
  db: Firestore,
  args: {
    businessId: string;
    type: TaxonomySuggestionType;
    adminUid: string;
    note?: string;
  }
) {
  await clearLinkedBusinessesRejectedTaxonomy(db, {
    type: args.type,
    businessIds: [args.businessId],
  });
}

/** ---------------- UNIQUENESS CHECK ---------------- */

export async function adminCategoryNameExists(db: Firestore, name: string) {
  const n = normalizeLabel(name);
  const col = collection(db, CATEGORIES);
  const q = query(
    col,
    where("isDeleted", "==", false),
    where("status", "in", ["approved", "pending"]),
    where("nameNormalized", "==", n),
    limit(1)
  );
  const snap = await getDocs(q);
  return !snap.empty;
}

export async function adminSubcategoryNameExists(
  db: Firestore,
  categoryId: string,
  name: string
) {
  const n = normalizeLabel(name);

  // ✅ nested path: categories/{categoryId}/subcategories
  const col = collection(db, CATEGORIES, categoryId, SUBCATEGORIES);

  const q = query(
    col,
    where("isDeleted", "==", false),
    where("status", "in", ["approved", "pending"]),
    where("nameNormalized", "==", n),
    limit(1)
  );

  const snap = await getDocs(q);
  return !snap.empty;
}

/** ---------------- EDIT / RENAME ---------------- */

export async function adminRenameCategory(
  db: Firestore,
  args: { categoryId: string; name: string; adminUid: string }
) {
  const nameNormalized = normalizeLabel(args.name);
  await updateDoc(doc(db, CATEGORIES, args.categoryId), {
    name: args.name.trim(),
    nameNormalized,
    updatedAt: serverTimestamp(),
    updatedBy: args.adminUid,
  });
}

export async function adminRenameSubcategory(
  db: Firestore,
  args: { categoryId: string; subcategoryId: string; name: string; adminUid: string }
) {
  const nameNormalized = normalizeLabel(args.name);

  // ✅ nested doc path: categories/{categoryId}/subcategories/{subcategoryId}
  await updateDoc(
    doc(db, CATEGORIES, args.categoryId, SUBCATEGORIES, args.subcategoryId),
    {
      name: args.name.trim(),
      nameNormalized,
      updatedAt: serverTimestamp(),
      updatedBy: args.adminUid,
    }
  );
}

/** ---------------- APPROVE / REJECT ---------------- */

export async function adminApproveCategory(
  db: Firestore,
  args: { categoryId: string; adminUid: string; note?: string }
) {
  await updateDoc(doc(db, CATEGORIES, args.categoryId), {
    status: "approved",
    reviewNote: args.note ?? "",
    reviewedAt: serverTimestamp(),
    reviewedBy: args.adminUid,
    updatedAt: serverTimestamp(),
    updatedBy: args.adminUid,
  });
}

export async function adminRejectCategory(
  db: Firestore,
  args: { categoryId: string; adminUid: string; note?: string }
) {
  await updateDoc(doc(db, CATEGORIES, args.categoryId), {
    status: "rejected",
    reviewNote: args.note ?? "",
    reviewedAt: serverTimestamp(),
    reviewedBy: args.adminUid,
    updatedAt: serverTimestamp(),
    updatedBy: args.adminUid,
  });
}

export async function adminApproveSubcategory(
  db: Firestore,
  args: { categoryId: string; subcategoryId: string; adminUid: string; note?: string }
) {
  await updateDoc(
    doc(db, CATEGORIES, args.categoryId, SUBCATEGORIES, args.subcategoryId),
    {
      status: "approved",
      reviewNote: args.note ?? "",
      reviewedAt: serverTimestamp(),
      reviewedBy: args.adminUid,
      updatedAt: serverTimestamp(),
      updatedBy: args.adminUid,
    }
  );
}

export async function adminRejectSubcategory(
  db: Firestore,
  args: { categoryId: string; subcategoryId: string; adminUid: string; note?: string }
) {
  await updateDoc(
    doc(db, CATEGORIES, args.categoryId, SUBCATEGORIES, args.subcategoryId),
    {
      status: "rejected",
      reviewNote: args.note ?? "",
      reviewedAt: serverTimestamp(),
      reviewedBy: args.adminUid,
      updatedAt: serverTimestamp(),
      updatedBy: args.adminUid,
    }
  );
}

/** ---------------- DELETE / RESTORE (soft delete) ---------------- */

export async function adminSoftDeleteCategory(
  db: Firestore,
  args: { categoryId: string; adminUid: string }
) {
  await updateDoc(doc(db, CATEGORIES, args.categoryId), {
    isDeleted: true,
    deletedAt: serverTimestamp(),
    deletedBy: args.adminUid,
    updatedAt: serverTimestamp(),
    updatedBy: args.adminUid,
  });
}

export async function adminRestoreCategory(
  db: Firestore,
  args: { categoryId: string; adminUid: string }
) {
  await updateDoc(doc(db, CATEGORIES, args.categoryId), {
    isDeleted: false,
    updatedAt: serverTimestamp(),
    updatedBy: args.adminUid,
  });
}

export async function adminSoftDeleteSubcategory(
  db: Firestore,
  args: { categoryId: string; subcategoryId: string; adminUid: string }
) {
  await updateDoc(
    doc(db, CATEGORIES, args.categoryId, SUBCATEGORIES, args.subcategoryId),
    {
      isDeleted: true,
      deletedAt: serverTimestamp(),
      deletedBy: args.adminUid,
      updatedAt: serverTimestamp(),
      updatedBy: args.adminUid,
    }
  );
}

export async function adminRestoreSubcategory(
  db: Firestore,
  args: { categoryId: string; subcategoryId: string; adminUid: string }
) {
  await updateDoc(
    doc(db, CATEGORIES, args.categoryId, SUBCATEGORIES, args.subcategoryId),
    {
      isDeleted: false,
      updatedAt: serverTimestamp(),
      updatedBy: args.adminUid,
    }
  );
}
